import { useContext, useEffect, useRef, useState } from "react";
import "./styles.scss";
import { useTranslation } from 'react-i18next';
import { resources, translations } from "../../../../../translations/translationsConstants";
import { WebEditorContext } from "../../../../../contexts";
import { TrashIcon } from "@heroicons/react/24/outline";
import { useEditorFile } from "../../../../../hooks";
import * as Constants from "../../../../../utils/constants";

export default function PageBreak({ editorPage, isLastPage }) {
    const { t: tc } = useTranslation([resources.common]);
    const { editorFileRef,
        showRightClickMenu,
        setRightClickMenu,
        autoSaveEditorFile } = useContext(WebEditorContext);
    const editorFileManager = useEditorFile();
    const [delayHandler, setDelayHandler] = useState(null);
    const [adjacentElements, setAdjacentElements] = useState();
    const disabled = editorFileRef.current?.DxmlFileStatus !== Constants.fileStatus.draft;

    if (isLastPage) {
        return <></>
    }

    const menuItems = [
        [
            {
                label: tc(translations.common.removePageBreak),
                icon: <TrashIcon />,
                onClick: () => {
                    editorFileManager.removePageBreak(editorFileRef.current, editorPage);
                    autoSaveEditorFile();

                    const pageBreakElement = document.getElementById(`pagebreak${editorPage.Id}`);
                    if (pageBreakElement)
                        pageBreakElement.blur();
                },
            },
        ],
    ];

    const handleContextMenu = (e) => {
        e.preventDefault(); // prevent the default behaviour when right clicked

        setRightClickMenu({
            position: {
                x: e.pageX,
                y: e.pageY,
            },
            items: menuItems,
        });
    }

    const handleMouseEnter = () => {
        setDelayHandler(setTimeout(() => {
            const editorPages = editorFileManager.getPages(editorFileRef.current);
            const currentPageIndex = editorPages.findIndex(x => x.Id === editorPage.Id);
            if (currentPageIndex < 0) return;

            const currentPage = editorPages[currentPageIndex];
            const elementsInCurrentPage = editorFileManager.getChildItemsToShow(currentPage);
            if (!elementsInCurrentPage?.length) return;

            const lastDxmlElementInCurrentPage = elementsInCurrentPage.at(-1);

            const nextPage = editorPages[currentPageIndex + 1];
            if (!nextPage) return;

            const elementsInNextPage = editorFileManager.getChildItemsToShow(nextPage);
            if (!elementsInNextPage?.length) return;

            const firstDxmlElementInNextPage = elementsInNextPage[0];

            const lastElementInCurrentPage = document.getElementById(`elementInPage${lastDxmlElementInCurrentPage.Id}`);
            if (!lastElementInCurrentPage) return;

            const firstElementInNextPage = document.getElementById(`elementInPage${firstDxmlElementInNextPage.Id}`);
            if (!firstElementInNextPage) return;

            lastElementInCurrentPage.style.border = "1px dashed #4338CA";
            firstElementInNextPage.style.border = "1px dashed #4338CA";

            setAdjacentElements([`elementInPage${lastDxmlElementInCurrentPage.Id}`, `elementInPage${firstDxmlElementInNextPage.Id}`]);
        }, 500))
    }

    const handleMouseLeave = () => {
        clearTimeout(delayHandler);
        if (adjacentElements?.length === 2) {
            const lastElementInCurrentPage = document.getElementById(adjacentElements[0]);
            if (!lastElementInCurrentPage) return;
            lastElementInCurrentPage.style.border = "";

            const firstElementInNextPage = document.getElementById(adjacentElements[1]);
            if (!firstElementInNextPage) return;
            firstElementInNextPage.style.border = "";
        }
        setAdjacentElements(null);
    }

    return (
        <div
            id={`pagebreak${editorPage.Id}`}
            role="separator"
            tabIndex={disabled ? -1 : 0}
            className={`page-break tw-my-3 tw-text-sm tw-text-gray-500 hover:tw-text-indigo-700 focus:tw-text-indigo-700 ${disabled ? "tw-pointer-events-none tw-cursor-not-allowed" : ""}`}
            onContextMenu={handleContextMenu}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <span className="">{tc(translations.common.pageBreak)}</span>
            <span id="context-hint" className="tw-hidden">{tc(translations.common.blockHelpText)}</span>
        </div>
    )
}